/**
 * Copiloot rechtsonder op de pagina Klanten: het v6-paarse vlak (zoals
 * het copilootvlak van Vandaag) met daarin het bestaande gesprek
 * (`WerkplekCopiloot`) met de klant als context. Begint leeg bij elke
 * klant (key op het klant-id). De v4-tokens die dat gesprek leest, staan
 * in `klanten.css` onder `.kl-copi` en wijzen naar de v6-tokens.
 */
import { useEffect, useRef, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { copilootQueryKey } from "../../Werkplek/Copiloot/copilootApi";
import { WerkplekCopiloot } from "../../Werkplek/Copiloot/WerkplekCopiloot";
import { Icoon } from "../Icoon";
import { v6Api } from "../api";
import { v6Sleutels } from "../sleutels";

/**
 * Controle blok 2 (punt 2): wordt de kaart na een wijziging opnieuw
 * opgehaald (dier toegevoegd, veld gezet), dan haalt de copiloot zijn
 * samenvatting ook opnieuw op. Anders zegt hij "Geen dier bekend" naast
 * een kaart met een dier.
 */
function useVolgDeKaart(klantId: string) {
  const queryClient = useQueryClient();
  // Zelfde sleutel als de kaart: deelt de cache, geen extra verzoek.
  const { dataUpdatedAt: bijgewerkt } = useQuery({
    queryKey: v6Sleutels.klantKaart(klantId),
    queryFn: () => v6Api.klantKaart(klantId),
    staleTime: Infinity,
  });
  const vorige = useRef(bijgewerkt);
  useEffect(() => {
    if (vorige.current && bijgewerkt > vorige.current) {
      void queryClient.invalidateQueries({
        queryKey: copilootQueryKey({ soort: "klant", id: klantId }),
      });
    }
    vorige.current = bijgewerkt;
  }, [bijgewerkt, klantId, queryClient]);
}

export function KlantCopiloot({ klantId, sluitKnop }: { klantId: string; sluitKnop?: ReactNode }) {
  useVolgDeKaart(klantId);
  return (
    <>
      <div className="ck">
        <span className="ic">
          <Icoon naam="copiloot" />
        </span>
        Copiloot
        <span style={{ marginLeft: "auto" }} />
        {sluitKnop}
      </div>
      <div className="kl-copi">
        <WerkplekCopiloot key={klantId} context={{ soort: "klant", id: klantId }} />
      </div>
    </>
  );
}
